import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Badge,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  NotificationsActive,
  NotificationsNone,
  MarkEmailRead,
  Delete,
  DoneAll,
} from '@mui/icons-material';
import { UserNotification, NotificationCounts } from '../types';
import { apiService } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [counts, setCounts] = useState<NotificationCounts>({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const [notificationsData, countsData] = await Promise.all([
        apiService.getNotifications(),
        apiService.getUnreadNotificationCount()
      ]);
      setNotifications(notificationsData);
      setCounts(countsData);
    } catch (err) {
      setError('Failed to load notifications');
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      setActionLoading(notificationId);
      await apiService.markNotificationAsRead(notificationId);
      
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
      
      setCounts(prev => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1)
      }));
      
      setSnackbar({
        open: true,
        message: 'Notification marked as read',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to mark notification as read',
        severity: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const markAllAsRead = async () => {
    try {
      setActionLoading('all');
      await apiService.markAllNotificationsAsRead();
      
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      setCounts(prev => ({ ...prev, unread: 0 }));
      
      setSnackbar({
        open: true,
        message: 'All notifications marked as read',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to mark all notifications as read',
        severity: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      setActionLoading(notificationId);
      await apiService.deleteNotification(notificationId);
      
      const deletedNotification = notifications.find(n => n.id === notificationId);
      
      setNotifications(prev =>
        prev.filter(notification => notification.id !== notificationId)
      );
      
      setCounts(prev => ({
        total: Math.max(0, prev.total - 1),
        unread: deletedNotification && !deletedNotification.read 
          ? Math.max(0, prev.unread - 1) 
          : prev.unread
      }));
      
      setSnackbar({
        open: true,
        message: 'Notification deleted',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete notification',
        severity: 'error'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'compass_shared':
        return '🧭';
      case 'note_added':
        return '📝';
      case 'account_linked':
        return '🔗';
      default:
        return '📢';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Notifications
          </Typography>
          <Badge badgeContent={counts.unread} color="primary">
            <NotificationsActive />
          </Badge>
        </Box>
        
        {counts.unread > 0 && (
          <Button
            variant="outlined"
            startIcon={<DoneAll />}
            onClick={markAllAsRead}
            disabled={actionLoading === 'all'}
          >
            {actionLoading === 'all' ? <CircularProgress size={20} /> : 'Mark All Read'}
          </Button>
        )}
      </Box>

      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          {counts.total} total notifications • {counts.unread} unread
        </Typography>
      </Box>

      {notifications.length === 0 ? (
        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <NotificationsNone sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No notifications yet
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                You'll receive notifications when someone shares a compass with you,
                adds notes, or other important activities occur.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <List>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              <ListItem
                sx={{
                  bgcolor: notification.read ? 'transparent' : 'action.hover',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Box display="flex" alignItems="flex-start" width="100%" gap={2}>
                  <Typography sx={{ fontSize: '1.5rem', mt: 0.5 }}>
                    {getNotificationIcon(notification.type)}
                  </Typography>
                  
                  <Box flexGrow={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="subtitle1" fontWeight={notification.read ? 'normal' : 'bold'}>
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Chip label="New" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {notification.message}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" gap={1}>
                    {!notification.read && (
                      <IconButton
                        size="small"
                        onClick={() => markAsRead(notification.id)}
                        disabled={actionLoading === notification.id}
                        title="Mark as read"
                      >
                        {actionLoading === notification.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <MarkEmailRead fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    
                    <IconButton
                      size="small"
                      onClick={() => deleteNotification(notification.id)}
                      disabled={actionLoading === notification.id}
                      title="Delete"
                    >
                      {actionLoading === notification.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Delete fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </ListItem>
              
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
